// Şirket ve tesis tanım drawer'ları.
//
// Yeni şirket TEK işlemde dört şey açıyor: şirket + ilk tesis + tesis
// sorumlusu + kütüphane kopyası. Eksik biri sistemi kullanılamaz bırakır —
// tesissiz şirkete kimse giremez, kullanıcısız tesis ölü kayıttır. Bu yüzden
// form tek parça; "önce şirketi aç, sonra tesis ekle" akışı yarım kurulum
// üretirdi.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'
import { tr } from '../../lib/trFormat'
import { sistem, type TenantSatiri, type TesisSatiri } from './api'

/** Sunucudaki KodKalibi ile aynı (TenantUclari.cs) — 409 beklemeden uyarır. */
const KOD = /^[A-Z0-9_-]{2,16}$/
const KOD_IPUCU = 'Büyük harf, rakam, tire ve alt çizgi · 2–16 karakter'

/** Sunucu 10 karakter altını reddediyor; kullanıcıyı istek atmadan durduruyoruz. */
const EN_KISA_SIFRE = 10

/**
 * E-posta kalıbı — sunucu yalnız "@" içeriyor mu diye bakıyor (TenantUclari).
 * Burada biraz daha sıkı: kullanıcı "ali@" yazıp 409 beklemesin.
 */
const EPOSTA = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Form alanı.
 *
 * <b>Otomatik doldurma KAPALI.</b> Bu form süper adminin BAŞKASI için hesap
 * açtığı yer; tarayıcı/parola yöneticisi burayı sıradan bir giriş formu sanıp
 * süper adminin kendi e-postasını "Ad soyad"a, kayıtlı şifresini de "Şifre"ye
 * dolduruyordu. Fark edilmezse tesis sorumlusu, süper adminin kimliğiyle
 * açılırdı.
 *
 * `autoComplete="off"` tek başına Chrome'da yetmiyor; şifre alanında
 * `new-password` gerekiyor ve alan adlarının tanıdık kalıplara (email, name,
 * username) benzememesi gerekiyor — `name` bu yüzden ön ekli.
 */
function Alan({
  etiket,
  deger,
  degisti,
  ipucu,
  tur = 'text',
  yerTutucu,
  buyukHarf,
  alanAdi,
  hata,
}: {
  etiket: string
  deger: string
  degisti: (v: string) => void
  ipucu?: string
  tur?: string
  yerTutucu?: string
  buyukHarf?: boolean
  alanAdi: string
  /** Doluysa alan `.fld.err` olur: kırmızı kenarlık, etiket ve ipucu. */
  hata?: string | null
}) {
  return (
    <div className={hata ? 'fld err' : 'fld'}>
      <label htmlFor={`sy-${alanAdi}`}>{etiket}</label>
      <input
        id={`sy-${alanAdi}`}
        name={`sy-${alanAdi}`}
        className={buyukHarf ? 'in mono' : 'in'}
        type={tur}
        value={deger}
        placeholder={yerTutucu}
        autoComplete={tur === 'password' ? 'new-password' : 'off'}
        autoCorrect="off"
        spellCheck={false}
        onChange={(e) => degisti(buyukHarf ? e.target.value.toUpperCase() : e.target.value)}
      />
      {(hata || ipucu) && <div className="fld-hint">{hata ?? ipucu}</div>}
    </div>
  )
}

/**
 * Kaydetmeyi engelleyen eksik/hatalı alanların listesi.
 *
 * Neden gerekli: düğmeyi pasif yapmak "neden" sorusunu cevaplamıyor. Kullanıcı
 * formu doldurup düğmenin ölü olduğunu görüyor ve hangi alanın sorunlu
 * olduğunu tek tek aramak zorunda kalıyordu. Liste, ipucu metinlerinin
 * tekrarı DEĞİL: alan ipucu "ne olmalı"yı, bu liste "şu an neyin eksik
 * olduğunu" söylüyor.
 */
function Engeller({ maddeler }: { maddeler: string[] }) {
  if (maddeler.length === 0) return null
  return (
    <div className="info-box" style={{ borderColor: 'var(--red-bd)', background: 'var(--red-bg)' }}>
      <b>Kaydedilemez.</b>
      <span>{maddeler.join(' · ')}</span>
    </div>
  )
}

export default function TenantDrawer({ mevcut }: { mevcut?: TenantSatiri }) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const duzenleme = !!mevcut

  const [unvan, setUnvan] = useState(mevcut?.unvan ?? '')
  const [kod, setKod] = useState(mevcut?.kod ?? '')
  const [vergiNo, setVergiNo] = useState(mevcut?.vergiNo ?? '')

  const [tesisKod, setTesisKod] = useState('')
  const [tesisAd, setTesisAd] = useState('')
  const [tesisIl, setTesisIl] = useState('')

  const [eposta, setEposta] = useState('')
  const [yoneticiAd, setYoneticiAd] = useState('')
  const [sifre, setSifre] = useState('')
  const [kutuphane, setKutuphane] = useState(true)

  const tazele = () => kuyruk.invalidateQueries({ queryKey: ['sistem-tenantlar'] })

  // Alan başına hata metni. `null` = sorun yok. Kullanıcı henüz yazmaya
  // BAŞLAMADIYSA kırmızı gösterilmiyor (dokunulmamış alan hata değildir);
  // engel listesi yine de eksiği söylüyor.
  const h = {
    unvan: unvan.trim().length > 0 && unvan.trim().length < 2 ? 'En az 2 karakter.' : null,
    kod:
      kod.trim().length > 0 && !KOD.test(kod.trim())
        ? `Geçersiz — ${KOD_IPUCU}`
        : null,
    tesisKod:
      tesisKod.trim().length > 0 && !KOD.test(tesisKod.trim())
        ? `Geçersiz — ${KOD_IPUCU}`
        : null,
    eposta:
      eposta.trim().length > 0 && !EPOSTA.test(eposta.trim())
        ? 'Geçerli bir e-posta adresi girin.'
        : null,
    sifre:
      sifre.length > 0 && sifre.length < EN_KISA_SIFRE
        ? `En az ${EN_KISA_SIFRE} karakter (şu an ${sifre.length}).`
        : null,
  }

  // Kaydetmeyi engelleyenler — boş alanlar da dâhil.
  const engeller: string[] = []
  if (unvan.trim().length < 2) engeller.push('Unvan girin')
  if (h.kod) engeller.push('Şirket kodu geçersiz')
  if (!duzenleme) {
    if (!tesisKod.trim()) engeller.push('Tesis kodu girin')
    else if (h.tesisKod) engeller.push('Tesis kodu geçersiz')
    if (!eposta.trim()) engeller.push('Yönetici e-postası girin')
    else if (h.eposta) engeller.push('E-posta geçersiz')
    if (!sifre) engeller.push('Şifre girin')
    else if (h.sifre) engeller.push(`Şifre en az ${EN_KISA_SIFRE} karakter olmalı`)
  }

  const gecerli = engeller.length === 0

  const kaydet = useMutation({
    mutationFn: async () => {
      if (duzenleme) {
        return sistem.tenantGuncelle(mevcut!.id, {
          unvan: unvan.trim(),
          kod: kod.trim() || null,
          vergiNo: vergiNo.trim() || null,
        })
      }
      return sistem.tenantOlustur({
        unvan: unvan.trim(),
        kod: kod.trim() || null,
        vergiNo: vergiNo.trim() || null,
        tesisKod: tesisKod.trim(),
        tesisAd: tesisAd.trim() || tesisKod.trim(),
        tesisIl: tesisIl.trim() || null,
        yoneticiEposta: eposta.trim(),
        yoneticiAd: yoneticiAd.trim() || eposta.trim(),
        yoneticiSifre: sifre,
        kutuphaneKopyala: kutuphane,
      })
    },
    onSuccess: (y) => {
      tazele()
      if (duzenleme) {
        toast(`${unvan.trim()} güncellendi`)
      } else {
        const k = 'kopya' in y ? y.kopya : null
        toast(
          `${unvan.trim()} açıldı · ${tesisKod.trim()} tesisi ve yönetici hesabı kuruldu` +
            (k
              ? ` · kütüphane: ${tr(k.uniteSinifi, 0)} sınıf, ${tr(k.stokKarti, 0)} kart, ${tr(k.esik, 0)} eşik`
              : ' · kütüphane kopyalanmadı'),
        )
      }
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Kaydedilemedi'),
  })

  return (
    <>
      <DrawerGovde>
        <div className="fgrid">
          <Alan
            etiket="Unvan"
            alanAdi="unvan"
            deger={unvan}
            degisti={setUnvan}
            yerTutucu="Örnek Enerji A.Ş."
            hata={h.unvan}
          />
          <Alan
            etiket="Şirket kodu"
            alanAdi="sirket-kodu"
            deger={kod}
            degisti={setKod}
            buyukHarf
            yerTutucu="ORN"
            ipucu={`İsteğe bağlı · ${KOD_IPUCU}`}
            hata={h.kod}
          />
          <Alan etiket="Vergi no" alanAdi="vkn" deger={vergiNo} degisti={setVergiNo} />
        </div>

        {!duzenleme && (
          <>
            <div className="sect-t">İlk Tesis</div>
            <div className="fgrid">
              <Alan
                etiket="Tesis kodu"
                alanAdi="tesis-kodu"
                deger={tesisKod}
                degisti={setTesisKod}
                buyukHarf
                yerTutucu="ORN-1"
                ipucu={KOD_IPUCU}
                hata={h.tesisKod}
              />
              <Alan etiket="Tesis adı" alanAdi="tesis-adi" deger={tesisAd} degisti={setTesisAd} />
              <Alan etiket="İl" alanAdi="il" deger={tesisIl} degisti={setTesisIl} />
            </div>

            <div className="sect-t">Tesis Sorumlusu</div>
            <div className="fgrid">
              <Alan
                etiket="E-posta"
                alanAdi="yonetici-adres"
                deger={eposta}
                degisti={setEposta}
                tur="email"
                ipucu="Giriş ekranında bu adres kullanılır · tüm şirketlerde benzersiz"
                hata={h.eposta}
              />
              <Alan etiket="Ad soyad" alanAdi="yonetici-isim" deger={yoneticiAd} degisti={setYoneticiAd} />
              <Alan
                etiket="Şifre"
                alanAdi="yonetici-giz"
                deger={sifre}
                degisti={setSifre}
                tur="password"
                ipucu="Kullanıcıya siz iletirsiniz; ilk girişte değiştirmesi önerilir"
                hata={h.sifre}
              />
            </div>

            {/*
              `.swrow` bir BUTON ve `.sw` anahtarın KENDİSİ (30×17 px pill),
              kap değil — `.swrow .sw{margin-left:auto}` onu sağa yaslıyor.
              Kalıp CariOrtak.tsx'teki rol anahtarlarıyla aynı.
            */}
            <button
              className="swrow"
              type="button"
              role="switch"
              aria-checked={kutuphane}
              onClick={() => setKutuphane((k) => !k)}
            >
              <span>Sistem kütüphanesini kopyala</span>
              <small>16 ünite sınıfı · stok kartı literatürü · §6.3 eşikleri</small>
              <span className={kutuphane ? 'sw on' : 'sw'} />
            </button>

            <Engeller maddeler={engeller} />

            <div className="info-box">
              <b>İlk sorumlu onay ve storno yetkili açılır.</b>
              <span>
                Aksi hâlde tesiste günü kapatabilecek kimse olmaz (K16) ve kurulum
                kilitlenir. Yetkiler sonradan Ayarlar'dan değiştirilebilir.
              </span>
            </div>
          </>
        )}
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-ghost" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={!gecerli || kaydet.isPending}
          title={gecerli ? undefined : `Eksik: ${engeller.join(' · ')}`}
          onClick={() => kaydet.mutate()}
        >
          {duzenleme ? 'Kaydet' : 'Şirketi aç'}
        </button>
      </DrawerAltlik>
    </>
  )
}

export function TesisDrawer({
  tenantId,
  mevcut,
}: {
  tenantId: string
  mevcut?: TesisSatiri
}) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const duzenleme = !!mevcut

  const [kod, setKod] = useState(mevcut?.kod ?? '')
  const [ad, setAd] = useState(mevcut?.ad ?? '')
  const [il, setIl] = useState(mevcut?.il ?? '')
  const [aktif, setAktif] = useState(mevcut?.aktif ?? true)
  const [kutuphane, setKutuphane] = useState(true)

  const kodHatasi =
    kod.trim().length > 0 && !KOD.test(kod.trim()) ? `Geçersiz — ${KOD_IPUCU}` : null
  const engeller: string[] = []
  if (!kod.trim()) engeller.push('Tesis kodu girin')
  else if (kodHatasi) engeller.push('Tesis kodu geçersiz')
  const gecerli = engeller.length === 0

  const kaydet = useMutation({
    mutationFn: () =>
      duzenleme
        ? sistem.tesisGuncelle(mevcut!.id, {
            kod: kod.trim(),
            ad: ad.trim() || kod.trim(),
            il: il.trim() || null,
            aktif,
          })
        : sistem.tesisOlustur(tenantId, {
            kod: kod.trim(),
            ad: ad.trim() || kod.trim(),
            il: il.trim() || null,
            kutuphaneKopyala: kutuphane,
          }),
    onSuccess: () => {
      kuyruk.invalidateQueries({ queryKey: ['sistem-tenantlar'] })
      toast(
        duzenleme
          ? `${kod.trim()} güncellendi${aktif ? '' : ' — açık oturumlar sonlandırıldı'}`
          : `${kod.trim()} tesisi açıldı`,
      )
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Kaydedilemedi'),
  })

  return (
    <>
      <DrawerGovde>
        <div className="fgrid">
          <Alan
            etiket="Tesis kodu"
            alanAdi="t-kodu"
            deger={kod}
            degisti={setKod}
            buyukHarf
            ipucu={KOD_IPUCU}
            hata={kodHatasi}
          />
          <Alan etiket="Tesis adı" alanAdi="t-adi" deger={ad} degisti={setAd} />
          <Alan etiket="İl" alanAdi="t-il" deger={il} degisti={setIl} />
        </div>

        {duzenleme ? (
          <button
            className="swrow"
            type="button"
            role="switch"
            aria-checked={aktif}
            onClick={() => setAktif((a) => !a)}
          >
            <span>Tesis aktif</span>
            <small>kapalıyken açık oturumlar sonlandırılır, tesis seçilemez</small>
            <span className={aktif ? 'sw on' : 'sw'} />
          </button>
        ) : (
          <button
            className="swrow"
            type="button"
            role="switch"
            aria-checked={kutuphane}
            onClick={() => setKutuphane((k) => !k)}
          >
            <span>Sistem kütüphanesini kopyala</span>
            <small>ünite sınıfları ve eşikler tesis bazlı; kendi kopyasını alır</small>
            <span className={kutuphane ? 'sw on' : 'sw'} />
          </button>
        )}
        <Engeller maddeler={engeller} />
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-ghost" type="button" onClick={() => kapat()}>
          Vazgeç
        </button>
        <button
          className="btn btn-pri"
          type="button"
          style={{ flex: 1, justifyContent: 'center' }}
          disabled={!gecerli || kaydet.isPending}
          title={gecerli ? undefined : `Eksik: ${engeller.join(' · ')}`}
          onClick={() => kaydet.mutate()}
        >
          {duzenleme ? 'Kaydet' : 'Tesisi aç'}
        </button>
      </DrawerAltlik>
    </>
  )
}
